import { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';

interface AdminRouteProps {
  children: React.ReactNode;
}

export function AdminRoute({ children }: AdminRouteProps) {
  const { user, loading } = useAuth();
  const [isAdmin, setIsAdmin] = useState<boolean | null>(null);
  const [checkingRole, setCheckingRole] = useState(true);

  useEffect(() => {
    const checkAdminRole = async () => {
      if (!user) {
        setIsAdmin(false);
        setCheckingRole(false);
        return;
      }

      try {
        // First try authoritative RPC with SECURITY DEFINER to bypass any RLS edge cases
        const { data: isAdminRpc, error: rpcError } = await supabase.rpc('is_admin', { user_id: user.id });
        if (rpcError) {
          console.warn('[AdminRoute] is_admin RPC error, falling back to user_roles:', rpcError);
        }
        if (isAdminRpc === true) {
          console.log('[AdminRoute] Admin detected via RPC', { userId: user.id });
          setIsAdmin(true);
        } else {
          // Fallback to user_roles table
          const { data: roles, error } = await supabase
            .from('user_roles')
            .select('role')
            .eq('user_id', user.id);

          if (error) {
            console.error('[AdminRoute] Error checking admin role via user_roles:', error);
            setIsAdmin(false);
          } else {
            const isAdminUser = Array.isArray(roles) && roles.some((r: any) => String(r.role) === 'admin');
            console.log('[AdminRoute] Admin via user_roles:', { roles, isAdminUser });
            setIsAdmin(isAdminUser);
          }
        }
      } catch (error) {
        console.error('Error checking admin role:', error);
        setIsAdmin(false);
      } finally {
        setCheckingRole(false);
      }
    };

    checkAdminRole();
  }, [user]);

  // Show loading while checking auth and role
  if (loading || checkingRole) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-lg">Loading...</div>
      </div>
    );
  }

  // Redirect if not authenticated
  if (!user) {
    return <Navigate to="/sign-in" replace />;
  }

  // If not admin, show an access denied message instead of redirecting (to avoid confusing loops)
  if (isAdmin === false) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center space-y-2">
          <h2 className="text-2xl font-semibold">Access denied</h2>
          <p className="text-muted-foreground">You don’t have permission to view this page.</p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}